import { Box, styled, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDebounce } from 'react-use';

import { EWidgetRegion, HubsCycleType, useIsPortraitMode } from '#shared';

import {
  useIsIdle,
  useData,
  useAppBehavior,
  useCasesSearchResult,
} from '../../data-context';
import { LayoutDropdown } from '../../LayoutDropdown';
import { NavigationMenu } from '../../NavigationMenu';
import { useAppDispatch } from '../../redux-store';
import { regionActions } from '../../slices';
import { toggleNewCasesBlocks } from '../../thunks';

import { MapLayoutViewToggle } from './MapLayoutViewToggle';
import { MapZoomControl } from './MapZoomControl';
import { Searchbar } from './Searchbar';
import { SettingsDropdown } from './SettingsDropdown';

const PREFIX = 'TSMED-Toolbar';

export const toolbarClasses = {
  root: `${PREFIX}-root`,
  left: `${PREFIX}-leftControls`,
  center: `${PREFIX}-centerControls`,
  right: `${PREFIX}-rightControls`,
  rightContainer: `${PREFIX}-rightContainer`,
};

const excludedProps: PropertyKey[] = ['isIdle', 'isPortraitMode'];

const Root = styled(Box, {
  shouldForwardProp: prop => !excludedProps.includes(prop),
})<{ isIdle: boolean; isPortraitMode: boolean }>(
  ({ theme: { spacing, breakpoints }, isIdle, isPortraitMode }) => ({
    [`&.${toolbarClasses.root}`]: {
      padding: spacing(1, 2),
      opacity: isIdle ? 0 : 1,
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: 0,
      zIndex: 10,
      pointerEvents: 'none',
      transition: 'opacity 0.2s ease-in-out',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',

      [breakpoints.down('xl')]: {
        padding: spacing(1),
      },

      ...(isPortraitMode && {
        [breakpoints.down('md')]: {
          top: 'var(--safe-area-top)',
        },
      }),
    },
    [`.${toolbarClasses.left}`]: {
      display: 'flex',
      flexDirection: 'row',
      gap: spacing(2),
      pointerEvents: 'auto',

      [breakpoints.down('xl')]: {
        gap: spacing(1),
      },
    },
    [`.${toolbarClasses.center}`]: {
      display: 'flex',
      flexDirection: 'row',
      gap: spacing(1),
      pointerEvents: 'auto',
    },
    [`.${toolbarClasses.right}`]: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing(1),
      alignItems: 'flex-end',
      pointerEvents: 'auto',
    },

    [`.${toolbarClasses.rightContainer}`]: {
      display: 'flex',
      flexDirection: 'row',
      gap: spacing(1),
      alignItems: 'center',
    },
  }),
);

interface ToolbarProps {
  /** Show/hide zoom controls */
  withZoom?: boolean;
  /** Show/hide searchbar */
  withSearchbar?: boolean;
  /** Show/hide layout dropdown */
  withLayoutDropdown?: boolean;
  /** Show/hide navigation menu */
  withNavigation?: boolean;
  /** Show/hide settings dropdown */
  withSettings?: boolean;
  /** Settings mode - show only settings dropdown in top-right corner */
  settingsMode?: boolean;
}

export const Toolbar = ({
  withZoom = true,
  withSearchbar = true,
  withLayoutDropdown,
  withNavigation = true,
  withSettings = true,
  settingsMode = false,
}: ToolbarProps) => {
  const dispatch = useAppDispatch();
  const { searchText, setSearchText } = useData();
  const searchResult = useCasesSearchResult();
  const isIdle = useIsIdle();
  const { clearHighlightSelection } = useAppBehavior();
  const [debouncedSearch, setDebouncedSearch] = useState(searchText);
  const location = useLocation();
  const theme = useTheme();
  const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));
  const isBigDevice = useMediaQuery(theme.breakpoints.up('xl'));
  const isCCScreen = useMediaQuery(theme.breakpoints.up('4k'));
  const isPortraitMode = useIsPortraitMode();

  const previousBreakpointRef = useRef({
    isBelowMd,
    isBigDevice,
    isPortraitMode,
  });
  const hubsCycleType = isCCScreen
    ? HubsCycleType.Extended
    : isBelowMd
      ? HubsCycleType.Short
      : HubsCycleType.Base;

  // Layout dropdown default depends on responsive breakpoint
  const shouldShowLayoutDropdown =
    withLayoutDropdown ?? (!isBelowMd && !isPortraitMode);

  useDebounce(
    () => {
      setSearchText(debouncedSearch);
      clearHighlightSelection();
    },
    300,
    [debouncedSearch],
  );

  // Handle breakpoint-based region adjustments
  useEffect(() => {
    const prev = previousBreakpointRef.current;
    const breakpointChanged =
      prev.isBelowMd !== isBelowMd ||
      prev.isBigDevice !== isBigDevice ||
      prev.isPortraitMode !== isPortraitMode;

    if (!breakpointChanged) {
      return;
    }

    previousBreakpointRef.current = { isBelowMd, isBigDevice, isPortraitMode };

    // Below md or portrait: Center only
    if (isBelowMd || isPortraitMode) {
      dispatch(
        regionActions.regionEnable({
          regionId: EWidgetRegion.Center,
          only: true,
        }),
      );
      dispatch(
        regionActions.regionShow({
          regionId: EWidgetRegion.Center,
          only: true,
        }),
      );
      dispatch(toggleNewCasesBlocks(false));
      return;
    }

    // md to xl (1024-1920): Left + Center only
    if (!isBigDevice) {
      dispatch(
        regionActions.regionEnable({
          regionId: [EWidgetRegion.Left, EWidgetRegion.Center],
          only: true,
        }),
      );
      dispatch(
        regionActions.regionShow({
          regionId: [EWidgetRegion.Left, EWidgetRegion.Center],
          only: true,
        }),
      );
      dispatch(toggleNewCasesBlocks(false));
      return;
    }

    // xl+ (1920+): Left + Center + Right
    dispatch(toggleNewCasesBlocks(true));
    dispatch(
      regionActions.regionEnable({
        regionId: [
          EWidgetRegion.Left,
          EWidgetRegion.Center,
          EWidgetRegion.Right,
        ],
        only: true,
      }),
    );

    dispatch(
      regionActions.regionShow({
        regionId: [
          EWidgetRegion.Left,
          EWidgetRegion.Center,
          EWidgetRegion.Right,
        ],
        only: true,
      }),
    );
  }, [isBelowMd, isBigDevice, isPortraitMode, dispatch]);

  return (
    <Root
      className={toolbarClasses.root}
      isIdle={isIdle}
      isPortraitMode={isPortraitMode}
      // Prevent all toolbar interactions from propagating to parent (drag handlers)
      onPointerDown={e => e.stopPropagation()}
      onPointerUp={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
      onMouseUp={e => e.stopPropagation()}
      onTouchStart={e => e.stopPropagation()}
      onTouchEnd={e => e.stopPropagation()}
    >
      {/* Settings mode: only render settings dropdown in top-right */}
      {settingsMode ? (
        <>
          <Box className={toolbarClasses.left}></Box>
          <Box className={toolbarClasses.center}></Box>
          <Box className={toolbarClasses.right}>
            {withSettings && <SettingsDropdown isIdle={isIdle} />}
          </Box>
        </>
      ) : (
        <>
          {/* Left side */}
          <Box className={toolbarClasses.left}>
            {withSearchbar && (
              <Searchbar
                value={debouncedSearch}
                onChange={e => setDebouncedSearch(e.target.value)}
                results={searchResult}
              />
            )}
            {withSettings && <SettingsDropdown isIdle={isIdle} />}
          </Box>

          {/* Center */}
          <Box className={toolbarClasses.center}></Box>

          {/* Right side */}
          <Box className={toolbarClasses.right}>
            <Box className={toolbarClasses.rightContainer}>
              {withNavigation && (
                <>
                  <MapLayoutViewToggle cycleType={hubsCycleType} />
                  {shouldShowLayoutDropdown && <LayoutDropdown />}
                  <NavigationMenu value={location.pathname} />
                </>
              )}
            </Box>

            {withZoom && <MapZoomControl />}
          </Box>
        </>
      )}
    </Root>
  );
};
